/**
 * Utilities for rendering multiple fills and strokes
 */

import { FillLayer, StrokeLayer, GradientFill } from '../editing/types';
import { applyGradientFill, isGradientFill } from './gradientUtils';

/**
 * Get the primary fill value from fills array or legacy fill property
 * For backward compatibility, supports both old format (fill: string) and new format (fills: FillLayer[])
 */
export function getPrimaryFill(
  fills: FillLayer[] | undefined,
  legacyFill: string | GradientFill | undefined
): string | GradientFill | undefined {
  if (fills && fills.length > 0) {
    const firstFill = fills[0];
    if (firstFill.type === 'solid' && firstFill.color) {
      return firstFill.color;
    } else if (firstFill.type === 'gradient' && firstFill.gradient) {
      return firstFill.gradient;
    }
  }
  // Fallback to legacy fill
  return legacyFill;
}

/**
 * Get the primary stroke value from strokes array or legacy stroke property
 */
export function getPrimaryStroke(
  strokes: StrokeLayer[] | undefined,
  legacyStroke: string | undefined
): string | undefined {
  if (strokes && strokes.length > 0) {
    return strokes[0].color;
  }
  return legacyStroke;
}

/**
 * Get the primary stroke width from strokes array or legacy strokeWidth property
 */
export function getPrimaryStrokeWidth(
  strokes: StrokeLayer[] | undefined,
  legacyStrokeWidth: number | undefined
): number | undefined {
  if (strokes && strokes.length > 0) {
    return strokes[0].width;
  }
  return legacyStrokeWidth;
}

/**
 * Normalize legacy fill/stroke data to new fills/strokes format
 * This helps with backward compatibility
 */
export function normalizeShapeData(data: any): {
  fills: FillLayer[];
  strokes: StrokeLayer[];
} {
  const fills: FillLayer[] = [];
  const strokes: StrokeLayer[] = [];

  // Handle fills
  if (data?.fills && Array.isArray(data.fills)) {
    fills.push(...data.fills);
  } else if (data?.fillGradient) {
    fills.push({
      type: 'gradient',
      gradient: data.fillGradient,
      opacity: data.opacity ?? 1,
    });
  } else if (data?.fill) {
    fills.push({
      type: 'solid',
      color: data.fill,
      opacity: data.opacity ?? 1,
    });
  } else {
    // Default fill
    fills.push({
      type: 'solid',
      color: '#FFFFFF',
      opacity: 1,
    });
  }

  // Handle strokes
  if (data?.strokes && Array.isArray(data.strokes)) {
    strokes.push(...data.strokes);
  } else if (data?.stroke) {
    strokes.push({
      color: data.stroke,
      width: data.strokeWidth || 2,
      style: data.strokeStyle,
      opacity: 1,
    });
  }

  return { fills, strokes };
}


